import { storeToRefs } from 'pinia';
import { lastValueFrom, map, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RouteLocationNormalized, RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';

import { debug } from '@silae/helpers/logger';
import { ROUTE_KEYS } from '~/router/router.domain';
import { useAuthStore } from '~/stores/auth.store';

export async function importRoutes(): Promise<RouteRecordRaw[]> {
	const routeFiles = import.meta.glob('../pages/*/*.route.ts');
	const importedRoutes = [];

	for (const path in routeFiles) {
		const routeModule = await routeFiles[path]();
		//@ts-ignore
		importedRoutes.push(routeModule?.default);
	}

	return importedRoutes;
}

function isRouteAuthorized(route: RouteLocationNormalized) {
	return !!(route.meta?.isAuthorized?.(route.params)?.value ?? true);
}

export function routerFactory(routes: RouteRecordRaw[]) {
	const router = createRouter({
		history: createWebHistory(import.meta.env.BASE_URL),
		routes
	});

	router.beforeEach(async (to, from) => {
		debug('routing', { to, from });
		const authStore = useAuthStore();
		const { idToken, authSession, impersonated, userProfile } = storeToRefs(authStore);
		const { fetchAuthSession, fetchAuthenticatedUserProfile, validateTokenExpiration, setImpersonation, signOut } = authStore;

		// check impersonation
		const idTokenQueryParam = to?.query?.id_token as string;
		const idTokenValue = to.name === ROUTE_KEYS.IMPERSONATION ? idTokenQueryParam : idToken.value;
		if (to.name === ROUTE_KEYS.IMPERSONATION && idTokenValue) {
			setImpersonation(true, idTokenValue);
		}

		if (to.meta?.auth) {
			let auth$;
			// case: user has been impersonated, we do not want to check authSession validity as there is no authSession
			if (impersonated.value) {
				auth$ = userProfile.value ? of(userProfile.value) : fetchAuthenticatedUserProfile();
			} else {
				auth$ = authSession.value && validateTokenExpiration(authSession.value) ? of(authSession.value) : fetchAuthSession();
			}
			return await lastValueFrom(
				auth$.pipe(
					map(() => {
						if (!isRouteAuthorized(to)) {
							// case: User tries to access an unauthorized page
							return { name: ROUTE_KEYS.HOME };
						}
					}),
					catchError(() => {
						if (!!authSession.value) {
							return signOut().pipe(switchMap(() => of({ name: ROUTE_KEYS.AUTH_SIGN_IN })));
						} else {
							return of({ name: ROUTE_KEYS.AUTH_SIGN_IN });
						}
					})
				)
			);
		}
	});

	return router;
}
